// ファイル名: recommend-products.js
// おすすめ商品セクション - ES6準拠インタラクション機能

/**
 * RECOMMEND商品セクションクラス
 * タブ切り替え、スクロールアニメーション、アクセシビリティ対応
 */
class RecommendProducts {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = sectionElement.dataset.sectionId;
    this.tabs = this.section.querySelectorAll('.recommend-tab');
    this.tabPanels = this.section.querySelectorAll('.recommend-grid');
    this.currentActiveIndex = 0;
    this.isAnimating = false;
    
    // 設定オプション
    this.options = {
      animationDuration: 500,
      observerThreshold: 0.2,
      keyboardEnabled: true,
      autoplayEnabled: false,
      autoplayInterval: 5000
    };

    this.init();
  }

  /**
   * 初期化処理
   */
  init() {
    this.setupEventListeners();
    this.setupIntersectionObserver();
    this.setupKeyboardNavigation();
    this.setupTouchGestures();
    this.announceToScreenReader('おすすめ商品セクションが読み込まれました');
    
    // 初期状態の設定
    this.setInitialActiveStates();
    
    console.log(`RecommendProducts initialized for section: ${this.sectionId}`);
  }

  /**
   * 初期アクティブ状態の設定
   */
  setInitialActiveStates() {
    if (this.tabs.length > 0 && this.tabPanels.length > 0) {
      this.tabs[0].classList.add('active');
      this.tabs[0].setAttribute('aria-selected', 'true');
      this.tabPanels[0].classList.add('active');
      this.currentActiveIndex = 0;
    }
  }

  /**
   * イベントリスナーの設定
   */
  setupEventListeners() {
    // タブクリックイベント
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchToTab(index);
      });

      // ホバーエフェクト
      tab.addEventListener('mouseenter', () => {
        if (!tab.classList.contains('active')) {
          this.animateTabHover(tab, true);
        }
      });

      tab.addEventListener('mouseleave', () => {
        if (!tab.classList.contains('active')) {
          this.animateTabHover(tab, false);
        }
      });
    });

    // 商品カードのクリック追跡（分析用）
    const productCards = this.section.querySelectorAll('.recommend-product-card');
    productCards.forEach((card) => {
      card.addEventListener('click', () => {
        const productId = card.dataset.productId;
        if (productId) {
          this.trackProductClick(productId);
        }
      });
    });

    // リサイズイベント
    window.addEventListener('resize', this.debounce(() => {
      this.handleResize();
    }, 250));
  }

  /**
   * キーボードナビゲーションの設定
   */
  setupKeyboardNavigation() {
    if (!this.options.keyboardEnabled) return;

    this.section.addEventListener('keydown', (e) => {
      // タブが選択されている場合のキーボード操作
      if (document.activeElement.classList.contains('recommend-tab')) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.navigateTab('prev');
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.navigateTab('next');
            break;
          case 'Home':
            e.preventDefault();
            this.switchToTab(0);
            break;
          case 'End':
            e.preventDefault();
            this.switchToTab(this.tabs.length - 1);
            break;
        }
      }
    });
  }

  /**
   * タッチジェスチャーの設定（スワイプ対応）
   */
  setupTouchGestures() {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    this.section.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.section.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX, minSwipeDistance);
    }, { passive: true });
  }

  /**
   * スワイプ処理
   */
  handleSwipe(startX, endX, minDistance) {
    const difference = startX - endX;
    
    if (Math.abs(difference) < minDistance) return;

    if (difference > 0) {
      // 左スワイプ - 次のタブ
      this.navigateTab('next');
    } else {
      // 右スワイプ - 前のタブ
      this.navigateTab('prev');
    }
  }

  /**
   * タブナビゲーション（前/次）
   */
  navigateTab(direction) {
    let nextIndex;
    
    if (direction === 'next') {
      nextIndex = (this.currentActiveIndex + 1) % this.tabs.length;
    } else {
      nextIndex = this.currentActiveIndex === 0 
        ? this.tabs.length - 1 
        : this.currentActiveIndex - 1;
    }
    
    this.switchToTab(nextIndex);
  }

  /**
   * 指定したタブに切り替え
   */
  switchToTab(targetIndex) {
    if (this.isAnimating || targetIndex === this.currentActiveIndex || targetIndex >= this.tabs.length) {
      return;
    }

    this.isAnimating = true;
    const previousIndex = this.currentActiveIndex;

    // WAI-ARIA属性の更新
    this.updateAriaAttributes(targetIndex, previousIndex);

    // タブの外観更新
    this.updateTabAppearance(targetIndex, previousIndex);

    // コンテンツパネルの切り替えアニメーション
    this.animateContentSwitch(targetIndex, previousIndex).then(() => {
      this.currentActiveIndex = targetIndex;
      this.isAnimating = false;
      
      // フォーカス管理
      this.tabs[targetIndex].focus();
      
      // スクリーンリーダーへの通知
      const tabName = this.tabs[targetIndex].textContent.trim();
      this.announceToScreenReader(`${tabName}タブが選択されました`);
    });
  }

  /**
   * ARIA属性の更新
   */
  updateAriaAttributes(activeIndex, previousIndex) {
    // 前のタブの属性をリセット
    this.tabs[previousIndex].setAttribute('aria-selected', 'false');
    this.tabs[previousIndex].setAttribute('tabindex', '-1');

    // 新しいアクティブタブの属性を設定
    this.tabs[activeIndex].setAttribute('aria-selected', 'true');
    this.tabs[activeIndex].setAttribute('tabindex', '0');
  }

  /**
   * タブの外観更新
   */
  updateTabAppearance(activeIndex, previousIndex) {
    // CSSクラスの更新
    this.tabs[previousIndex].classList.remove('active');
    this.tabs[activeIndex].classList.add('active');

    // 動的スタイルの適用（JSON設定から）
    const section = this.section;
    const activeTab = this.tabs[activeIndex];
    const previousTab = this.tabs[previousIndex];

    // スタイルの取得と適用は、既存のCSS変数やインラインスタイルを使用
    requestAnimationFrame(() => {
      this.animateTabTransition(activeTab, previousTab);
    });
  }

  /**
   * タブトランジションアニメーション
   */
  animateTabTransition(activeTab, previousTab) {
    // アクティブタブのアニメーション
    activeTab.style.transform = 'translateY(-2px) scale(1.02)';
    activeTab.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

    // 前のタブのリセット
    previousTab.style.transform = 'translateY(0) scale(1)';
    previousTab.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

    // アニメーション完了後のクリーンアップ
    setTimeout(() => {
      activeTab.style.transform = '';
      previousTab.style.transform = '';
    }, 300);
  }

  /**
   * タブホバーアニメーション
   */
  animateTabHover(tab, isHovering) {
    if (isHovering) {
      tab.style.transform = 'translateY(-1px)';
      tab.style.transition = 'transform 0.2s ease';
    } else {
      tab.style.transform = '';
    }
  }

  /**
   * コンテンツ切り替えアニメーション
   */
  async animateContentSwitch(activeIndex, previousIndex) {
    const activePanel = this.tabPanels[activeIndex];
    const previousPanel = this.tabPanels[previousIndex];

    // フェードアウト
    if (previousPanel) {
      previousPanel.style.opacity = '0';
      previousPanel.style.transform = 'translateY(20px)';
      
      await this.delay(150);
      
      previousPanel.classList.remove('active');
    }

    // フェードイン
    if (activePanel) {
      activePanel.classList.add('active');
      activePanel.style.opacity = '0';
      activePanel.style.transform = 'translateY(20px)';
      
      await this.delay(50);
      
      activePanel.style.opacity = '1';
      activePanel.style.transform = 'translateY(0)';
      
      // 商品カードのスタガードアニメーション
      this.animateProductCards(activePanel);
    }
  }

  /**
   * 商品カードのスタガードアニメーション
   */
  animateProductCards(panel) {
    const cards = panel.querySelectorAll('.recommend-product-card');
    
    cards.forEach((card, index) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(30px)';
      
      setTimeout(() => {
        card.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, index * 100);
    });
  }

  /**
   * Intersection Observerの設定（スクロールアニメーション）
   */
  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.triggerScrollAnimation();
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: this.options.observerThreshold,
      rootMargin: '0px 0px -50px 0px'
    });

    observer.observe(this.section);
  }

  /**
   * スクロールアニメーションのトリガー
   */
  triggerScrollAnimation() {
    // ヘッダーアニメーション
    const header = this.section.querySelector('.recommend-header');
    if (header) {
      header.style.animation = 'fadeInUp 0.8s ease forwards';
    }

    // タブアニメーション
    this.tabs.forEach((tab, index) => {
      setTimeout(() => {
        tab.style.animation = 'slideIn 0.5s ease forwards';
      }, index * 100);
    });

    // アクティブパネルの商品カードアニメーション
    const activePanel = this.section.querySelector('.recommend-grid.active');
    if (activePanel) {
      this.animateProductCards(activePanel);
    }
  }

  /**
   * 商品クリック追跡（分析用）
   */
  trackProductClick(productId) {
    // Google Analytics 4 イベント送信
    if (typeof gtag !== 'undefined') {
      gtag('event', 'select_content', {
        content_type: 'product',
        content_id: productId,
        custom_parameters: {
          section_type: 'recommend_products',
          section_id: this.sectionId
        }
      });
    }

    // Shopify Analytics
    if (typeof analytics !== 'undefined' && analytics.track) {
      analytics.track('Product Clicked', {
        product_id: productId,
        section: 'recommend_products',
        section_id: this.sectionId
      });
    }

    console.log(`Product clicked: ${productId} in section: ${this.sectionId}`);
  }

  /**
   * リサイズ処理
   */
  handleResize() {
    // タブのスクロール可能領域の調整
    const tabsContainer = this.section.querySelector('.recommend-tabs');
    if (tabsContainer) {
      this.adjustTabsScroll(tabsContainer);
    }

    // グリッドレイアウトの再計算
    this.recalculateLayout();
  }

  /**
   * タブスクロール調整
   */
  adjustTabsScroll(container) {
    const activeTab = container.querySelector('.recommend-tab.active');
    if (activeTab) {
      const containerRect = container.getBoundingClientRect();
      const activeTabRect = activeTab.getBoundingClientRect();
      
      if (activeTabRect.left < containerRect.left || activeTabRect.right > containerRect.right) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }

  /**
   * レイアウト再計算
   */
  recalculateLayout() {
    // 高さの再計算
    const activePanel = this.section.querySelector('.recommend-grid.active');
    if (activePanel) {
      const contentContainer = this.section.querySelector('.recommend-content');
      contentContainer.style.minHeight = `${activePanel.scrollHeight}px`;
    }
  }

  /**
   * スクリーンリーダーへの通知
   */
  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * デバウンス関数
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Promise delay ユーティリティ
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * セクション破棄処理
   */
  destroy() {
    // イベントリスナーの削除
    this.tabs.forEach(tab => {
      tab.replaceWith(tab.cloneNode(true));
    });

    // オブザーバーの削除
    if (this.observer) {
      this.observer.disconnect();
    }

    console.log(`RecommendProducts destroyed for section: ${this.sectionId}`);
  }
}

/**
 * セクション初期化
 * Shopifyのセクション読み込み完了時に実行
 */
document.addEventListener('DOMContentLoaded', () => {
  initializeRecommendSections();
});

// Shopify Theme Editorでのセクション更新対応
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.recommend-section')) {
    initializeRecommendSections();
  }
});

/**
 * 全てのRECOMMENDセクションを初期化
 */
function initializeRecommendSections() {
  const sections = document.querySelectorAll('.recommend-section');
  
  sections.forEach((section) => {
    // 既存のインスタンスがあれば破棄
    if (section.recommendInstance) {
      section.recommendInstance.destroy();
    }
    
    // 新しいインスタンスを作成
    section.recommendInstance = new RecommendProducts(section);
  });
}

/**
 * パフォーマンス監視
 */
if (typeof PerformanceObserver !== 'undefined') {
  const perfObserver = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (entry.name.includes('recommend-products')) {
        console.log(`Performance: ${entry.name} took ${entry.duration}ms`);
      }
    });
  });
  
  perfObserver.observe({ entryTypes: ['measure'] });
}

// エクスポート（モジュール環境用）
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RecommendProducts;
}